import { FC, MutableRefObject, ReactNode, useEffect, useState, useMemo, useRef, useReducer, useCallback } from 'react'
import { useHistory } from 'react-router-dom';
import shared from './app-edit-price-fields.module.scss';
import styles from './app-edit-categories.module.scss';
import { serviceAreas } from '../../services/service-areas';
import { useTranslation } from 'react-i18next';
import Field from '../../components/shared/Field/Field';
import Modal from '../../components/shared/Modal/Modal';
import BtButton from '../bt-button/bt-button';
import { NotificationManager } from 'react-notifications';
import { TrashIcon, SparkleIcon } from '../../components/svgs';
import { ReactComponent as EditIcon } from '../../assets/svg/editIcon.svg';
import Selector from '../../components/shared/Selector/Selector';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import appState from '../../state/AppStateContainer';
import DataTable from '../../components/shared/DataTable/DataTable';
import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';
import SuggestionChip from '../../components/shared/SuggestionChip/SuggestionChip';

type Area = {
  name: string;
  _id: string;
  isActive: boolean;
  accountNumber?: string;
  slug?: string;
  description?: string;
  suggestedAreas?: { areaId: string; source?: SuggestionSource }[];
  suggestionRun?: { at?: string; nameAtRun?: string };
}

type CategoryItem = {
  id?: string;
  description?: string;
  label: string;
  value: string; // value is the id of the category/area
  accountNumber?: string;
  linkedCategory?: string[]; // Array of linked super admin category names
  slug?: string;
}

type EditDraft = {
  name: string;
  accountNumber: string;
  description: string;
  linkedCategory: CategoryItem[];
}

// How a pair was found: 'exact' is a deterministic normalized-name match resolved without
// the model, 'ai' came from the model. Kept per entry because a category is resolved by
// exactly one route - it is never partly exact and partly AI.
// Matches the house default used by the other paged tables (10-15 rows).
const CATEGORIES_PER_PAGE = 15;

// How long the editor draft outlives cancel/save. material-table closes the panel with
// a MUI Collapse whose 'auto' duration scales with content height; 400ms comfortably
// covers this panel, and the draft must keep rendering until then or the panel goes
// blank mid-animation.
const PANEL_CLOSE_MS = 400;

type SuggestionSource = 'exact' | 'ai';
type SuggestionEntry = { categoryIds: string[]; source: SuggestionSource };
type SuggestionsMap = Record<string, SuggestionEntry>;

type TFunc = (key: string, defaultValue?: string, options?: Record<string, unknown>) => string;

type EditorApi = {
  t: TFunc;
  t2: TFunc;
  superAdminCategories: CategoryItem[];
  isSuperAdminOrganization: boolean;
  draft: EditDraft | null;
  nameError: string;
  editingRowId: string | null;
  savingRowId: string | null;
  suggestedIds: Set<string>;
  patchDraft: (patch: Partial<EditDraft>) => void;
  scheduleNameCheck: (selfId: string) => void;
  saveEdit: (row: CategoryItem) => void;
  cancelEdit: () => void;
  dismissDraftSuggestion: (row: CategoryItem) => void;
  suggestingRowId: string | null;
  suggestDraftLink: (row: CategoryItem) => void;
}

/**
 * Hover label in the app's tooltip style. Replaces `title=""`, whose native rendering
 * (black OS box, no styling, slow delay) does not match the rest of the UI.
 */
const HoverTip: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <TooltipContainer
    renderReferenceComponent={(className, ref) => (
      <span className={`${className} ${styles.tooltipTrigger}`} ref={ref}>{children}</span>
    )}
  >
    <div className={styles.tooltipLabel}>{label}</div>
  </TooltipContainer>
);

const AddAreaForm: FC<{
  categories: CategoryItem[];
  superAdminCategories: CategoryItem[];
  loading: boolean;
  isSuperAdminOrganization: boolean;
  onSave: (data: { name: string; accountNumber: string; description: string; linkedCategory: CategoryItem[] }) => Promise<void>;
  onClose: () => void;
}> = ({ categories, superAdminCategories, loading, isSuperAdminOrganization, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [description, setDescription] = useState('');
  const [linkedCategory, setLinkedCategory] = useState<CategoryItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedIds, setSuggestedIds] = useState<Set<string>>(new Set());
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories' });
  const [t2] = useTranslation('supplierDatabase');

  const categoryAlreadyExists = useMemo(() => {
    if (!name.trim()) return false;
    return categories.some(
      cat => cat.label.trim().toLowerCase() === name.trim().toLowerCase()
    )
  }, [name, categories, superAdminCategories]);

  const trimmedName = name.trim();
  const canSuggest = !!trimmedName && !loading && !saving && !suggesting;
  const hasSuggestion = suggestedIds.size > 0;

  const clearSuggestion = () => {
    setLinkedCategory(prev => prev.filter(cat => !suggestedIds.has(cat.value)));
    setSuggestedIds(new Set());
  };

  const handleSuggest = async () => {
    if (!canSuggest) return;
    setSuggesting(true);
    try {
      const result = await serviceAreas.suggestCategoryLinks({ areaIds: [], names: [trimmedName] });
      const entry = (result?.suggestions ?? []).find((s: { key: string }) => s.key === 'name:0');
      const returnedIds = new Set(entry?.categoryIds ?? []);
      const existingIds = new Set(linkedCategory.map(cat => cat.value));
      const additions = superAdminCategories.filter(cat => returnedIds.has(cat.value) && !existingIds.has(cat.value));
      if (!additions.length) {
        NotificationManager.info(t('suggest.notification.noMatchForName', 'Inget förslag hittades för det namnet'));
        return;
      }
      setLinkedCategory(prev => [...prev, ...additions]);
      setSuggestedIds(prev => {
        const next = new Set(Array.from(prev));
        additions.forEach(cat => next.add(cat.value));
        return next;
      });
    } catch (error: any) {
      const errorKey = error?.response?.data?.error;
      if (errorKey === 'error.aiNotConfigured') {
        NotificationManager.error(t('suggest.notification.notConfigured', 'AI-förslag är inte tillgängliga för tillfället. Vänligen försök igen senare.'));
      } else {
        console.error('Error fetching link suggestion:', error);
        NotificationManager.error(t('suggest.notification.error', 'Kunde inte hämta förslag'));
      }
    } finally {
      setSuggesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || categoryAlreadyExists) return;
    setSaving(true);

    try {
      await onSave({ name: name.trim(), accountNumber: accountNumber.trim(), description: description.trim(), linkedCategory });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.addForm}>
      <Field
        label={t('edit.categoryNameLabel', 'Kategorinamn')}
        value={name}
        onChange={(value) => setName(value)}
        placeholder={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
        required
        errorMessage={
          categoryAlreadyExists && !saving && !saved
            ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
            : undefined
        }
      />
      <Field
        multiline
        className={styles.descriptionField}
        label={<>
          {t('edit.descriptionLabel', 'Beskrivning')}
          <span className={styles.optionalTag}>{t('edit.optionalTag', '(valfritt)')}</span>
        </>}
        value={description}
        onChange={(value: string) => setDescription(value)}
        placeholder={t('edit.descriptionPlaceholder', 'Vad omfattar kategorin? Hjälper AI-matchningen att skilja liknande kategorier åt.')}
      />
      <Field
        label={t2('addArea.label.accountNumber', 'Kontonummer')}
        value={accountNumber}
        onChange={(value) => setAccountNumber(value)}
        placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
      />
      {!isSuperAdminOrganization && (
        <div>
          <div className={styles.linkRow}>
            <div className={styles.linkRowField}>
              <Selector
                isDisabled={loading}
                label={t('edit.linkToSuperAdminCategory', 'Länka till global kategori')}
                placeholder={t('edit.selectPlaceholder', 'Välj')}
                isMulti={true}
                options={superAdminCategories}
                onChange={(selected) => setLinkedCategory((selected as CategoryItem[]) || [])}
                value={linkedCategory}
                styles={{
                  multiValue: (base: any, state: any) => suggestedIds.has(state.data?.value)
                    ? { ...base, backgroundColor: 'var(--green-50)', border: '1px solid var(--green-200)', borderRadius: '12px' }
                    : base,
                  multiValueLabel: (base: any, state: any) => suggestedIds.has(state.data?.value)
                    ? { ...base, color: 'var(--green-700)' }
                    : base,
                }}
              />
            </div>
            <HoverTip label={t('suggest.byNameButton', 'Föreslå koppling utifrån namnet')}>
              <BtButton
                color="white"
                disabled={!canSuggest}
                loaderShow={suggesting}
                onClick={handleSuggest}
              >
                {!suggesting && <SparkleIcon size="16px" color="var(--gray-700)" />}
              </BtButton>
            </HoverTip>
          </div>
          {hasSuggestion && (
            <div className={styles.suggestionHint}>
              <SparkleIcon size="12px" color="var(--gray-500)" withPlus={false} />
              <span>{t('suggest.loadedHint', 'AI-förslag laddat. Justera eller lägg till kopplingar, spara sedan.')}</span>
              <button
                type="button"
                onClick={clearSuggestion}
                className={styles.linkButton}
                disabled={saving || suggesting}
              >
                {t('suggest.dismissSuggestion', 'Avvisa förslag')}
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.formActions}>
        <BtButton color="white" onClick={onClose} disabled={saving || suggesting}>
          {t('edit.cancel', 'Avbryt')}
        </BtButton>
        <BtButton
          color="primary"
          onClick={handleSubmit}
          disabled={!name.trim() || categoryAlreadyExists || saving || suggesting}
          loaderShow={saving}
        >
          {saving ? t2('addArea.button.saving', 'Sparar...') : t2('addArea.button.save', 'Spara yrkeskategori')}
        </BtButton>
      </div>
    </div>
  );
};

const CategoryRowEditor: FC<{ row: CategoryItem; apiRef: MutableRefObject<EditorApi> }> = ({ row, apiRef }) => {
  const api = apiRef.current;
  const draft = api?.draft;
  if (!api || !draft) return null;

  const { t, t2 } = api;
  const saving = api.savingRowId === row.value;
  const suggestingDraft = api.suggestingRowId === row.value;
  const hasSuggestion = api.suggestedIds.size > 0;
  // A suggestion request in flight blocks save/cancel/dismiss: its result is about to
  // patch the draft, and cancelling or saving mid-flight would race it.
  const busy = saving || suggestingDraft;
  const disableSave = !draft.name.trim() || !!api.nameError || busy;
  // Editing has ended but the panel is still collapsing over this content; render it
  // frozen so a stray click can't re-save or re-cancel mid-animation.
  const closing = api.editingRowId !== row.value;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !busy) api.saveEdit(row);
    if (e.key === 'Escape' && !busy) api.cancelEdit();
  };

  // Enter inserts a newline in the textarea, so only Escape is intercepted there.
  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !busy) api.cancelEdit();
  };

  return (
    <div className={`${styles.rowEditor} ${closing ? styles.rowEditorClosing : ''}`.trim()}>
      <div className={`${styles.rowEditorGrid} ${api.isSuperAdminOrganization ? styles.rowEditorGridCompact : ''}`.trim()}>
        <Field
          label={t('edit.categoryNameLabel', 'Kategorinamn')}
          required
          value={draft.name}
          onChange={(value) => { api.patchDraft({ name: value }); api.scheduleNameCheck(row.value); }}
          onKeyDown={handleKeyDown}
          errorMessage={api.nameError || undefined}
        />
        <Field
          className={styles.accountField}
          label={t2('addArea.label.accountNumber', 'Kontonummer')}
          value={draft.accountNumber}
          onChange={(value) => api.patchDraft({ accountNumber: value })}
          onKeyDown={handleKeyDown}
          placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
        />
        {!api.isSuperAdminOrganization && (
          <>
            <div className={styles.rowEditorSelector}>
              {/* The tooltip lists every linked category, since isOverflow keeps the
                  control to a single line and folds the rest into a "+N" badge. The
                  tooltip opens upward while the menu opens downward, so they don't
                  collide. With nothing selected the children are false and
                  TooltipContainer skips the portal entirely. */}
              <TooltipContainer
                renderReferenceComponent={(className, ref) => (
                  <div ref={ref} className={className}>
                    <Selector
                      isMulti
                      isSearchable
                      isOverflow
                      label={t('columns.linkedCategories', 'Kopplade globala kategorier')}
                      options={api.superAdminCategories}
                      value={draft.linkedCategory}
                      onChange={(selected) => api.patchDraft({ linkedCategory: (selected as CategoryItem[]) || [] })}
                      placeholder={t('edit.selectPlaceholder', 'Välj')}
                      styles={{
                        multiValue: (base: any, state: any) => api.suggestedIds.has(state.data?.value)
                          ? { ...base, backgroundColor: 'var(--green-50)', border: '1px solid var(--green-200)', borderRadius: '12px' }
                          : base,
                        multiValueLabel: (base: any, state: any) => api.suggestedIds.has(state.data?.value)
                          ? { ...base, color: 'var(--green-700)' }
                          : base,
                      }}
                    />
                  </div>
                )}
              >
                {draft.linkedCategory.length > 0 && (
                  <div className={styles.selectorTooltip}>
                    {draft.linkedCategory.map((linked) => (
                      <span key={linked.value} className={styles.linkedChip}>{linked.label}</span>
                    ))}
                  </div>
                )}
              </TooltipContainer>
            </div>
            <HoverTip label={t('suggest.byNameButton', 'Föreslå koppling utifrån namnet')}>
              <BtButton
                color="white"
                class={styles.suggestButton}
                disabled={!draft.name.trim() || saving || suggestingDraft}
                loaderShow={suggestingDraft}
                onClick={() => api.suggestDraftLink(row)}
              >
                {!suggestingDraft && <SparkleIcon size="16px" color="var(--gray-700)" />}
              </BtButton>
            </HoverTip>
          </>
        )}
      </div>
      {/* Full width below the grid: this is a sentence, not a value. */}
      <Field
        multiline
        className={styles.descriptionField}
        label={<>
          {t('edit.descriptionLabel', 'Beskrivning')}
          <span className={styles.optionalTag}>{t('edit.optionalTag', '(valfritt)')}</span>
        </>}
        value={draft.description}
        onChange={(value: string) => api.patchDraft({ description: value })}
        onKeyDown={handleDescriptionKeyDown}
        placeholder={t('edit.descriptionPlaceholder', 'Vad omfattar kategorin? Hjälper AI-matchningen att skilja liknande kategorier åt.')}
      />
      <div className={styles.rowEditorActions}>
        {hasSuggestion && (
          <>
            <SparkleIcon size="14px" color="var(--gray-700)" />
            <span className={styles.rowEditorHint}>
              {t('suggest.loadedHint', 'AI-förslag laddat. Justera eller lägg till kopplingar, spara sedan.')}
            </span>
          </>
        )}
        <span className={styles.spacer} />
        {hasSuggestion && (
          <BtButton color="white" size="xxs" onClick={() => api.dismissDraftSuggestion(row)} disabled={busy}>
            {t('suggest.dismissSuggestion', 'Avvisa förslag')}
          </BtButton>
        )}
        <BtButton color="white" size="xxs" onClick={api.cancelEdit} disabled={busy}>
          {t('edit.cancel', 'Avbryt')}
        </BtButton>
        <BtButton size="xxs" onClick={() => api.saveEdit(row)} disabled={disableSave} loaderShow={saving}>
          {saving ? t('edit.saving', 'Sparar...') : t('edit.save', 'Spara')}
        </BtButton>
      </div>
    </div>
  );
};

const AppEditCategories: FC<{
  addNewAreaModal: boolean;
  setAddNewAreaModal: (show: boolean) => void;
}> = ({ addNewAreaModal, setAddNewAreaModal }) => {
  const history = useHistory();
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [superAdminCategories, setSuperAdminCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories'});
  const [t2] = useTranslation('supplierDatabase');
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalKey, setAddModalKey] = useState(0);
  const [editingRowId, setEditingRowId] = useState<string | null>(null); // CategoryItem.value
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [suggestingRowId, setSuggestingRowId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionsMap | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [acceptingRowId, setAcceptingRowId] = useState<string | null>(null);
  const editDraftRef = useRef<EditDraft | null>(null);
  const draftSuggestedIdsRef = useRef<Set<string>>(new Set());
  const editingRowIdRef = useRef<string | null>(null);
  // Which row's panel still renders editor content. Outlives editingRowIdRef by
  // PANEL_CLOSE_MS so the closing collapse animates over the editor, not a blank.
  const panelRowIdRef = useRef<string | null>(null);
  const panelCleanupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableRef = useRef<any>(null);
  const [, forceDraftRender] = useReducer((x: number) => x + 1, 0);
  const nameErrorRef = useRef('');
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      const [allAreas, allSuperAdminAreas] = await Promise.all([
        serviceAreas.getAreas(),
        serviceAreas.fetchSuperAdminAreas()
      ]);

      let mappedCategories = (allAreas ?? []).map((x: Area) => ({
        id: x._id,
        label: x.name,
        value: x._id,
        accountNumber: x.accountNumber,
        description: x.description,
        linkedCategory: [] as string[],
        slug: x.slug
      })) as CategoryItem[];


      allSuperAdminAreas?.forEach((item: any) => {
        item.customerAreas?.forEach((x: any) => {
          const categoryIndex = mappedCategories.findIndex(c => c.value === x.areaId);
          if (categoryIndex !== -1) {
            if (!mappedCategories[categoryIndex].linkedCategory) {
              mappedCategories[categoryIndex].linkedCategory = [];
            }
            mappedCategories[categoryIndex].linkedCategory!.push(item.name);
          }
        });
      });

      setCategories(mappedCategories);

      setSuperAdminCategories(allSuperAdminAreas?.map((x: Area) => ({
        label: x.name,
        value: x._id,
      })) ?? []);

      // Rehydrate suggestions stored by an earlier run so they survive a reload.
      const validIds = new Set((allSuperAdminAreas ?? []).map((x: Area) => x._id));
      const stored: SuggestionsMap = {};
      (allAreas ?? []).forEach((area: Area) => {
        // No run recorded at all is different from a run that matched nothing.
        if (!area.suggestionRun?.at) return;
        // Renaming the category invalidates a suggestion computed for the old name.
        if (area.suggestionRun.nameAtRun && area.suggestionRun.nameAtRun !== area.name) return;
        // A suggested global category may have been deleted since the run.
        const live = (area.suggestedAreas ?? []).filter(entry => validIds.has(entry.areaId));
        stored[area._id] = {
          categoryIds: live.map(entry => entry.areaId),
          // One run resolves a category by a single route, so the entries agree.
          source: live[0]?.source === 'exact' ? 'exact' : 'ai',
        };
      });
      if (Object.keys(stored).length) {
        setSuggestions(stored);
      }

      setLoading(false);
    })();

  }, []);

  const updateCategoryLinks = useCallback(async (
    customerAreaId: string,
    currentLinks: string[],
    newLinks: CategoryItem[]
  ) => {
    const currentLinkIds = currentLinks
      .map(name => superAdminCategories.find(cat => cat.label === name)?.value)
      .filter(Boolean) as string[];

    const newLinkIds = newLinks.map(cat => cat.value);

    const toLink = newLinkIds.filter(id => !currentLinkIds.includes(id));
    const toUnlink = currentLinkIds.filter(id => !newLinkIds.includes(id));

    for (const superAdminId of toLink) {
      await serviceAreas.linkCategoryToSuperAdminCategory(superAdminId, customerAreaId);
    }

    for (const superAdminId of toUnlink) {
      await serviceAreas.unlinkCategoryFromSuperAdminCategory(superAdminId, customerAreaId);
    }
  }, [superAdminCategories]);

  const handleSaveNewCategory = async (data: { name: string; accountNumber: string; description: string; linkedCategory: CategoryItem[] }) => {
    try {
      const updatedAreas = await serviceAreas.addArea({
        name: data.name,
        accountNumber: data.accountNumber || undefined,
        description: data.description || undefined
      }) as Area[] | null;

      if (updatedAreas && Array.isArray(updatedAreas)) {
        const addedCategory = updatedAreas.find(x => x.name === data.name);

        if (addedCategory) {
          await updateCategoryLinks(addedCategory._id, [], data.linkedCategory);

          NotificationManager.success(t2('notification.success.addArea', 'Yrkeskategori tillagd'));

          setCategories(prev => updatedAreas.map((x: Area) => {
            const existing = prev.find(p => p.value === x._id);
            return {
              id: x._id,
              label: x.name,
              value: x._id,
              accountNumber: x.accountNumber,
              description: x.description,
              slug: x.slug,
              linkedCategory: x._id === addedCategory._id
                ? data.linkedCategory.map(cat => cat.label)
                : existing?.linkedCategory ?? []
            };
          }));
        }

        handleCloseAddModal();
      }
    } catch (error) {
      console.error('Error adding category:', error);
      NotificationManager.error(t2('notification.error.addArea', 'Kunde inte lägga till yrkeskategori'));
    }
  };

  const handleCloseAddModal = () => {
    setAddNewAreaModal(false);
    setAddModalKey(prev => prev + 1);
  };

  const setPanelOpen = useCallback((rowId: string, open: boolean) => {
    const materialTable = tableRef.current;
    if (!materialTable) return;
    const sortedRows = materialTable.dataManager?.sortedData ?? [];
    const index = sortedRows.findIndex((r: CategoryItem) => r.value === rowId);
    if (index === -1) return;
    const isOpen = !!(sortedRows[index] as any).tableData?.showDetailPanel;
    if (isOpen !== open) {
      materialTable.onToggleDetailPanel([index], materialTable.props.detailPanel);
    }
  }, []);

  const cancelEdit = useCallback(() => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    if (editingRowIdRef.current) setPanelOpen(editingRowIdRef.current, false);
    editingRowIdRef.current = null;
    nameErrorRef.current = '';
    setEditingRowId(null);
    setSavingRowId(null);
    // The draft stays for the closing animation; drop it once the panel has
    // collapsed - unless a new edit has started and owns the refs by then.
    if (panelCleanupTimer.current) clearTimeout(panelCleanupTimer.current);
    panelCleanupTimer.current = setTimeout(() => {
      if (editingRowIdRef.current) return;
      panelRowIdRef.current = null;
      editDraftRef.current = null;
      draftSuggestedIdsRef.current = new Set();
      forceDraftRender();
    }, PANEL_CLOSE_MS);
  }, [setPanelOpen]);

  const patchDraft = useCallback((patch: Partial<EditDraft>) => {
    if (!editDraftRef.current) return;
    editDraftRef.current = { ...editDraftRef.current, ...patch };
    forceDraftRender();
  }, []);

  const nameError = useCallback((name: string, selfId: string) =>
    !name.trim()
      ? t('edit.nameRequired', 'Kategorinamn krävs')
      : categories.some(c => c.value !== selfId &&
        c.label.trim().toLowerCase() === name.trim().toLowerCase())
        ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
        : '', [categories, t, t2]);

  const NAME_CHECK_DEBOUNCE_MS = 500;
  const scheduleNameCheck = useCallback((selfId: string) => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    nameCheckTimer.current = setTimeout(() => {
      const draft = editDraftRef.current;
      nameErrorRef.current = draft ? nameError(draft.name, selfId) : '';
      forceDraftRender();
    }, NAME_CHECK_DEBOUNCE_MS);
  }, [nameError]);

  const startEdit = useCallback((row: CategoryItem, withSuggestion: boolean) => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    const existingLinks = row.linkedCategory
      ? superAdminCategories.filter(cat => row.linkedCategory?.includes(cat.label))
      : [];

    let suggestedItems: CategoryItem[] = [];
    if (withSuggestion) {
      const suggestedIds = suggestions?.[row.value]?.categoryIds ?? [];
      const existingIds = new Set(existingLinks.map(cat => cat.value));
      suggestedItems = superAdminCategories.filter(cat => suggestedIds.includes(cat.value) && !existingIds.has(cat.value));
    }

    editDraftRef.current = {
      name: row.label,
      accountNumber: row.accountNumber || '',
      description: row.description || '',
      linkedCategory: [...existingLinks, ...suggestedItems]
    };
    draftSuggestedIdsRef.current = new Set(suggestedItems.map(cat => cat.value));
    nameErrorRef.current = '';
    // A pending post-close cleanup would clear the refs this edit now owns.
    if (panelCleanupTimer.current) clearTimeout(panelCleanupTimer.current);
    editingRowIdRef.current = row.value;
    panelRowIdRef.current = row.value;
    setEditingRowId(row.value);
    setPanelOpen(row.value, true);
  }, [superAdminCategories, suggestions, setPanelOpen]);

  // Clear any pending timers on unmount.
  useEffect(() => () => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    if (panelCleanupTimer.current) clearTimeout(panelCleanupTimer.current);
  }, []);

  const removeSuggestionEntry = useCallback((areaId: string) => {
    setSuggestions(prev => {
      if (!prev) return prev;
      if (!(areaId in prev)) return prev;
      const { [areaId]: _removed, ...rest } = prev;
      return Object.keys(rest).length ? rest : null;
    });
  }, []);

  // A deliberate dismissal by the user, as opposed to a suggestion being consumed by an
  // accept (the link endpoints clear those server-side). Cleared locally first so the UI
  // stays responsive; if the request fails the entry reappears on the next reload.
  const dismissSuggestions = useCallback(async (areaIds: string[]) => {
    if (!areaIds.length) return;
    areaIds.forEach(removeSuggestionEntry);
    try {
      await serviceAreas.dismissCategorySuggestions(areaIds);
    } catch (error) {
      console.error('Error dismissing link suggestions:', error);
    }
  }, [removeSuggestionEntry]);

  const filteredCategories = useMemo(() => categories
    .filter((category) => {
      if (!searchTerm.trim()) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        category.label.toLowerCase().includes(searchLower) ||
        category.accountNumber?.toLowerCase().includes(searchLower) ||
        category.linkedCategory?.some(link => link.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => a.label.localeCompare(b.label)), [categories, searchTerm]);

  // "No match" entries are run feedback, not work the user must resolve. Once the last
  // real suggestion in the filter is accepted or dismissed they are stale: they leave
  // with it, so the banner disappears with the action and the categories become
  // eligible for a fresh run instead of demanding a manual "dismiss all" first.
  // Every path that resolves a row's REAL suggestion must apply this sweep - the
  // table-row buttons, accept-all, and the row editor (chip click -> save / "Avvisa
  // förslag") all do.
  const trailingNoMatchIds = useCallback((resolvedId: string) => {
    const remaining = filteredCategories
      .map(category => category.value)
      .filter(id => id !== resolvedId && suggestions?.[id]);
    return remaining.some(id => suggestions?.[id]?.categoryIds.length) ? [] : remaining;
  }, [filteredCategories, suggestions]);

  const saveEdit = useCallback(async (row: CategoryItem) => {
    const draft = editDraftRef.current;
    if (!draft) return;
    const validationError = nameError(draft.name, row.value);
    if (validationError) {
      if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
      nameErrorRef.current = validationError;
      forceDraftRender();
      return;
    }

    // Snapshot before the awaits. Guarded on the row actually carrying a real
    // suggestion: a plain edit of an unrelated row during a no-match-only state must
    // not wipe the stored runs (that would forfeit the freshness reuse for everyone).
    const trailing = suggestions?.[row.value]?.categoryIds.length ? trailingNoMatchIds(row.value) : [];
    setSavingRowId(row.value);
    try {
      await serviceAreas.updateAreaDetails(row.value, {
        name: draft.name.trim(),
        accountNumber: draft.accountNumber.trim() || undefined,
        description: draft.description.trim()
      });

      await updateCategoryLinks(row.value, row.linkedCategory || [], draft.linkedCategory);

      setCategories(prev => prev.map(p => p.value === row.value
        ? {
          ...p,
          label: draft.name.trim(),
          accountNumber: draft.accountNumber.trim() || undefined,
          description: draft.description.trim() || undefined,
          linkedCategory: draft.linkedCategory.map(cat => cat.label)
        }
        : p));
      removeSuggestionEntry(row.value);
      if (trailing.length) dismissSuggestions(trailing);

      NotificationManager.success(t('notification.success.edit', 'Kategori uppdaterad'));
      cancelEdit();
    } catch (error) {
      console.error('Error updating category:', error);
      NotificationManager.error(t('notification.error.edit', 'Kunde inte uppdatera kategori'));
    } finally {
      setSavingRowId(null);
    }
  }, [nameError, updateCategoryLinks, cancelEdit, removeSuggestionEntry, suggestions, trailingNoMatchIds, dismissSuggestions, t]);

  // Remove the AI-suggested links from the open editor but keep it open for manual editing.
  const dismissDraftSuggestion = useCallback((row: CategoryItem) => {
    const draft = editDraftRef.current;
    if (draft) {
      const suggestedIds = draftSuggestedIdsRef.current;
      editDraftRef.current = {
        ...draft,
        linkedCategory: draft.linkedCategory.filter(cat => !suggestedIds.has(cat.value)),
      };
    }
    draftSuggestedIdsRef.current = new Set();
    // A per-row dismiss like the table button: dismissing the last real suggestion in
    // the filter takes the no-match leftovers with it.
    const trailing = suggestions?.[row.value]?.categoryIds.length ? trailingNoMatchIds(row.value) : [];
    dismissSuggestions([row.value, ...trailing]);
    forceDraftRender();
  }, [dismissSuggestions, suggestions, trailingNoMatchIds]);

  const suggestDraftLink = useCallback(async (row: CategoryItem) => {
    const draftName = editDraftRef.current?.name.trim();
    if (!draftName) return;
    setSuggestingRowId(row.value);
    try {
      const result = await serviceAreas.suggestCategoryLinks({ areaIds: [], names: [draftName] });
      const entry = (result?.suggestions ?? []).find((s: { key: string }) => s.key === 'name:0');
      const suggestedIds = new Set(entry?.categoryIds ?? []);
      const current = editDraftRef.current;
      if (!current) return; // editor closed while the request was in flight
      const existingIds = new Set(current.linkedCategory.map(cat => cat.value));
      // Append only: an existing link is never replaced by a suggestion.
      const additions = superAdminCategories.filter(cat => suggestedIds.has(cat.value) && !existingIds.has(cat.value));
      if (!additions.length) {
        NotificationManager.info(t('suggest.notification.noMatchForName', 'Inget förslag hittades för det namnet'));
        return;
      }
      editDraftRef.current = { ...current, linkedCategory: [...current.linkedCategory, ...additions] };
      // Union, so chips from an earlier suggestion stay marked alongside the new ones.
      const nextSuggested = new Set(Array.from(draftSuggestedIdsRef.current));
      additions.forEach(cat => nextSuggested.add(cat.value));
      draftSuggestedIdsRef.current = nextSuggested;
      forceDraftRender();
    } catch (error: any) {
      const errorKey = error?.response?.data?.error;
      if (errorKey === 'error.aiNotConfigured') {
        NotificationManager.error(t('suggest.notification.notConfigured', 'AI-förslag är inte tillgängliga för tillfället. Vänligen försök igen senare.'));
      } else {
        console.error('Error fetching link suggestion:', error);
        NotificationManager.error(t('suggest.notification.error', 'Kunde inte hämta förslag'));
      }
    } finally {
      setSuggestingRowId(null);
    }
  }, [superAdminCategories, t]);

  const handleDelete = useCallback(async (category: CategoryItem) => {
    if (!window.confirm(t('delete.confirm', 'Är du säker på att du vill ta bort {{name}}?', { name: category.label }))) {
      return;
    }

    // Deleting the row that carried the last real suggestion resolves it too.
    const trailing = suggestions?.[category.value]?.categoryIds.length ? trailingNoMatchIds(category.value) : [];
    try {
      await serviceAreas.deleteArea(category.value);
      setCategories(prev => prev.filter(p => p.value !== category.value));
      removeSuggestionEntry(category.value);
      if (trailing.length) dismissSuggestions(trailing);
      NotificationManager.success(t('notification.success.delete', 'Kategori borttagen'));
    } catch (error) {
      console.error('Error deleting category:', error);
      NotificationManager.error(t('notification.error.delete', 'Kunde inte ta bort kategori'));
    }
  }, [t, removeSuggestionEntry, suggestions, trailingNoMatchIds, dismissSuggestions]);

  const runSuggestLinks = useCallback(async () => {
    // Only unresolved real suggestions block a re-request. Categories whose last run
    // found nothing stay eligible - the backend freshness window keeps a quick retry
    // from paying the model twice for the same empty answer.
    const pending = filteredCategories.filter(category => !suggestions?.[category.value]?.categoryIds.length);
    if (!pending.length) {
      NotificationManager.info(t('suggest.notification.nothingToSuggest', 'Inga nya förslag hittades. Prova att lägga till mer information, eller godkänn eller avvisa befintliga förslag, innan du begär fler.'));
      return;
    }

    setSuggesting(true);
    try {
      const [result, allSuperAdminAreas] = await Promise.all([
        serviceAreas.suggestCategoryLinks({ areaIds: pending.map(category => category.value) }),
        serviceAreas.fetchSuperAdminAreas(),
      ]);
      const freshSuperAdminCategories: CategoryItem[] = (allSuperAdminAreas ?? []).map((x: Area) => ({
        label: x.name,
        value: x._id,
      }));
      setSuperAdminCategories(freshSuperAdminCategories);
      const validIds = new Set(freshSuperAdminCategories.map(cat => cat.value));
      const byAreaId: SuggestionsMap = {};
      (result?.suggestions ?? []).forEach((s: { key: string, categoryIds: string[], source?: string }) => {
        if (s.key.startsWith('name:')) return;
        byAreaId[s.key] = {
          categoryIds: (s.categoryIds ?? []).filter(id => validIds.has(id)),
          source: s.source === 'exact' ? 'exact' : 'ai',
        };
      });
      if (!Object.keys(byAreaId).length) {
        NotificationManager.info(t('suggest.notification.nothingToSuggest', 'Inga nya förslag hittades. Prova att lägga till mer information, eller godkänn eller avvisa befintliga förslag, innan du begär fler.'));
        return;
      }
      setSuggestions(prev => ({ ...(prev ?? {}), ...byAreaId }));
      if (!Object.values(byAreaId).some(entry => entry.categoryIds.length)) {
        NotificationManager.info(t('suggest.notification.nothingToSuggest', 'Inga nya förslag hittades. Prova att lägga till mer information, eller godkänn eller avvisa befintliga förslag, innan du begär fler.'));
      }
    } catch (error: any) {
      const errorKey = error?.response?.data?.error;
      if (errorKey === 'error.aiNotConfigured') {
        NotificationManager.error(t('suggest.notification.notConfigured', 'AI-förslag är inte tillgängliga för tillfället. Vänligen försök igen senare.'));
      } else {
        console.error('Error fetching link suggestions:', error);
        NotificationManager.error(t('suggest.notification.error', 'Kunde inte hämta förslag'));
      }
    } finally {
      setSuggesting(false);
    }
  }, [filteredCategories, suggestions, t]);

  const acceptRowSuggestion = useCallback(async (row: CategoryItem, suggestedIds: string[]) => {
    const existingLinks = row.linkedCategory
      ? superAdminCategories.filter(cat => row.linkedCategory?.includes(cat.label))
      : [];
    const existingIds = new Set(existingLinks.map(cat => cat.value));
    const suggestedItems = superAdminCategories.filter(cat => suggestedIds.includes(cat.value) && !existingIds.has(cat.value));
    if (!suggestedItems.length) {
      removeSuggestionEntry(row.value);
      return;
    }

    await updateCategoryLinks(row.value, row.linkedCategory || [], [...existingLinks, ...suggestedItems]);

    setCategories(prev => prev.map(p => p.value === row.value
      ? { ...p, linkedCategory: [...(p.linkedCategory ?? []), ...suggestedItems.map(cat => cat.label)] }
      : p));
    removeSuggestionEntry(row.value);
  }, [superAdminCategories, updateCategoryLinks, removeSuggestionEntry]);

  const acceptSuggestion = useCallback(async (row: CategoryItem) => {
    const suggestedIds = suggestions?.[row.value]?.categoryIds;
    if (!suggestedIds?.length) return;
    // Snapshot before the accept: if this was the last real suggestion in the filter,
    // the leftover no-match entries go with it.
    const trailing = trailingNoMatchIds(row.value);
    setAcceptingRowId(row.value);
    try {
      await acceptRowSuggestion(row, suggestedIds);
      if (trailing.length) dismissSuggestions(trailing);
    } catch (error) {
      console.error('Error applying link suggestion:', error);
      NotificationManager.error(t('suggest.notification.acceptError', 'Kunde inte tillämpa förslaget'));
    } finally {
      setAcceptingRowId(null);
    }
  }, [suggestions, acceptRowSuggestion, trailingNoMatchIds, dismissSuggestions, t]);

  const acceptAllSuggestions = useCallback(async () => {
    if (!suggestions) return;
    setAcceptingAll(true);
    try {
      const visibleIds = new Set(filteredCategories.map(category => category.value));
      const entries = Object.entries(suggestions)
        .filter(([, entry]) => entry.categoryIds.length)
        .map(([areaId, entry]) => [areaId, entry.categoryIds] as const);
      // Snapshot up front: the loop only touches real suggestions, so the scoped
      // no-match entries are unaffected by it and cleared as one batch at the end.
      const scopedNoMatchIds = filteredCategories
        .map(category => category.value)
        .filter(id => suggestions[id] && !suggestions[id].categoryIds.length);
      for (const [areaId, suggestedIds] of entries) {
        const row = categories.find(c => c.value === areaId);
        if (!row) {
          // The category was removed since the run - drop the orphan entry.
          removeSuggestionEntry(areaId);
          continue;
        }
        if (!visibleIds.has(areaId)) continue;
        await acceptRowSuggestion(row, suggestedIds);
      }
      // Accepting the run resolves it: the no-match leftovers leave with it, so the
      // banner disappears instead of counting down into "no match found", and those
      // categories are immediately eligible for a fresh run.
      if (scopedNoMatchIds.length) dismissSuggestions(scopedNoMatchIds);
      NotificationManager.success(t('suggest.notification.acceptedAll', 'Förslagen har tillämpats'));
    } catch (error) {
      console.error('Error applying link suggestions:', error);
      NotificationManager.error(t('suggest.notification.acceptAllError', 'Kunde inte tillämpa förslagen'));
    } finally {
      setAcceptingAll(false);
    }
  }, [suggestions, categories, filteredCategories, acceptRowSuggestion, removeSuggestionEntry, dismissSuggestions, t]);

  const dismissAllSuggestions = useCallback(() => {
    // Symmetrical with accept-all: drop only what is on screen, keep the rest pending.
    const visible = filteredCategories
      .map(category => category.value)
      .filter(areaId => suggestions?.[areaId]);
    dismissSuggestions(visible);
  }, [filteredCategories, suggestions, dismissSuggestions]);

  const url = appState._getCurrentOrgUrl();
  const isSuperAdminOrganization = appState.isSuperAdmin() && appState.getCurrentOrgSlug() === 'superadmin';

  useEffect(() => {
    if (editingRowId !== null && !filteredCategories.some(c => c.value === editingRowId)) {
      cancelEdit();
    }
  }, [editingRowId, filteredCategories, cancelEdit]);

  const handleRowClick = (_event?: React.MouseEvent, rowData?: CategoryItem) => {
    if (editingRowId !== null) return; // don't navigate while a row is being edited
    if (isSuperAdminOrganization && rowData?.slug) {
      history.push(`${url}/categories/${rowData.slug}`);
    }
  };

  const visibleSuggestionIds = useMemo(
    () => filteredCategories.map(category => category.value).filter(id => suggestions?.[id]),
    [filteredCategories, suggestions]
  );
  const suggestionCount = visibleSuggestionIds.filter(id => suggestions?.[id]?.categoryIds.length).length;
  const suggestionCategoryCount = visibleSuggestionIds.length;

  // Everything CategoryRowEditor needs, refreshed every render. The detail panel's
  // render function is stored by material-table when the panel opens, so it must not
  // close over state directly (see CategoryRowEditor).
  const editorApiRef = useRef<EditorApi>(null as unknown as EditorApi);
  editorApiRef.current = {
    t: t as unknown as TFunc,
    t2: t2 as unknown as TFunc,
    superAdminCategories,
    isSuperAdminOrganization,
    draft: editDraftRef.current,
    nameError: nameErrorRef.current,
    editingRowId,
    savingRowId,
    suggestedIds: draftSuggestedIdsRef.current,
    patchDraft,
    scheduleNameCheck,
    saveEdit,
    cancelEdit,
    dismissDraftSuggestion,
    suggestingRowId,
    suggestDraftLink,
  };

  const renderRowEditor = useCallback(({ rowData }: { rowData: CategoryItem }) => {
    // Gated on panelRowIdRef rather than editingRowIdRef: it stays set for
    // PANEL_CLOSE_MS after save/cancel so the closing collapse has content.
    if (rowData.value !== panelRowIdRef.current) return null;
    return <CategoryRowEditor row={rowData} apiRef={editorApiRef} />;
  }, []);

  // A running bulk suggest counts too: renaming or deleting a row mid-run would race
  // the run's persistence and merge, so row actions lock until it lands.
  const uiBusy = editingRowId !== null || acceptingAll || acceptingRowId !== null || suggesting;

  const tableColumns = useMemo(() => [
    {
      title: t('columns.name', 'Namn'),
      field: 'label',
      width: '40%',
      render: (rowData: CategoryItem) => (
        <div>
          <div className={styles.cellName}>{rowData.label}</div>
          {rowData.description && (
            <div className={styles.cellDescription}>{rowData.description}</div>
          )}
        </div>
      ),
      customSort: (a: CategoryItem, b: CategoryItem) => a.label.localeCompare(b.label),
    },
    {
      title: t('columns.accountNumber', 'Kontonummer'),
      field: 'accountNumber',
      width: '180px',
      render: (rowData: CategoryItem) => rowData.accountNumber
        ? <span className={styles.cellAccount}>{rowData.accountNumber}</span>
        : <span className={styles.cellPlaceholder}>–</span>,
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order?: 'asc' | 'desc') => {
        const hasA = a.accountNumber?.trim();
        const hasB = b.accountNumber?.trim();

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return parseFloat(a.accountNumber!) - parseFloat(b.accountNumber!);
      },
    },
    {
      title: t('columns.linkedCategories', 'Kopplade globala kategorier'),
      field: 'linkedCategory',
      render: (rowData: CategoryItem) => {
        const rowSuggestions = suggestions?.[rowData.value];
        const isExact = rowSuggestions?.source === 'exact';

        if (rowSuggestions?.categoryIds.length) {
          return (
            <div className={styles.chipList}>
              {rowSuggestions.categoryIds.map(id => {
                const category = superAdminCategories.find(cat => cat.value === id);
                if (!category) return null;
                return (
                  <SuggestionChip
                    key={id}
                    label={category.label}
                    variant={isExact ? 'exact' : 'ai'}
                    title={isExact
                      ? t('suggest.chipTooltipExact', 'Exakt namnmatchning – klicka för att justera')
                      : t('suggest.chipTooltip', 'AI-förslag – klicka för att justera')}
                    onClick={!uiBusy ? () => startEdit(rowData, true) : undefined}
                  />
                );
              })}
            </div>
          );
        }

        if (rowSuggestions && !rowSuggestions.categoryIds.length) {
          return (
            <span className={styles.noMatchCell}>
              <span className={styles.noMatchLabel}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                  <path d="M12 11v5" />
                  <path d="M12 8v.5" />
                </svg>
                {t('suggest.noMatch', 'Ingen träff hittades')}
              </span>
              <button
                type="button"
                disabled={uiBusy}
                className={styles.linkButton}
                onClick={(e) => { e.stopPropagation(); startEdit(rowData, false); }}
              >
                {t('suggest.addManually', 'Lägg till manuellt')}
              </button>
            </span>
          );
        }

        const links = rowData.linkedCategory ?? [];
        if (!links.length) {
          return <span className={styles.cellPlaceholder}>–</span>;
        }
        return (
          <div className={styles.chipList}>
            {links.map(name => (
              <span key={name} className={styles.linkedChip}>{name}</span>
            ))}
          </div>
        );
      },
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order?: 'asc' | 'desc') => {
        const lengthA = a.linkedCategory?.length ?? 0;
        const lengthB = b.linkedCategory?.length ?? 0;
        const hasA = lengthA > 0;
        const hasB = lengthB > 0;

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return lengthA - lengthB;
      },
      hidden: isSuperAdminOrganization,
    },
    {
      title: '',
      field: 'actions',
      sorting: false,
      width: '92px',
      render: (rowData: CategoryItem) => {
        const rowSuggestions = suggestions?.[rowData.value];

        if (rowSuggestions?.categoryIds.length) {
          const accepting = acceptingRowId === rowData.value;
          return (
            <div
              className={styles.rowActions}
              onClick={(e) => e.stopPropagation()}
            >
              <HoverTip label={t('suggest.acceptRow', 'Godkänn förslag')}>
                <BtButton
                  color="white"
                  size="xxs"
                  disabled={uiBusy && !accepting}
                  loaderShow={accepting}
                  onClick={() => acceptSuggestion(rowData)}
                >
                  {!accepting && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green-700)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12l5 5 9-11" />
                    </svg>
                  )}
                </BtButton>
              </HoverTip>
              <HoverTip label={t('suggest.dismissRow', 'Avvisa förslag')}>
                <BtButton
                  color="white"
                  size="xxs"
                  disabled={uiBusy}
                  onClick={() => dismissSuggestions([rowData.value, ...trailingNoMatchIds(rowData.value)])}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </BtButton>
              </HoverTip>
            </div>
          );
        }

        return (
          <div
            className={styles.rowActions}
            onClick={(e) => e.stopPropagation()}
          >
            <HoverTip label={t('menu.edit', 'Redigera')}>
              <BtButton color="white" size="xxs" disabled={uiBusy} onClick={() => startEdit(rowData, false)}>
                <EditIcon className={styles.actionIcon} />
              </BtButton>
            </HoverTip>
            <HoverTip label={t('menu.delete', 'Ta bort')}>
              <BtButton color="white" size="xxs" disabled={uiBusy} onClick={() => handleDelete(rowData)}>
                <TrashIcon size="14px" color="var(--gray-700)" />
              </BtButton>
            </HoverTip>
          </div>
        );
      }
    }
  ], [uiBusy, acceptingRowId, suggestions, superAdminCategories, isSuperAdminOrganization, t, startEdit, acceptSuggestion, dismissSuggestions, trailingNoMatchIds, handleDelete]);

  return <div>
    <div className={styles.introRow}>
      <p className={shared.intro}>
        {t('intro', 'Här kan du byta namn på eller ta bort yrkeskategorier. Namnbyten påverkar både befintliga upphandlingar och leverantörer. Borttagna kategorier försvinner från leverantörer men befintliga upphandlingar påverkas inte.')}
      </p>
    </div>

    <SearchBarWithActions
      value={searchTerm}
      onChange={setSearchTerm}
      className={styles.searchBar}
    >
      {!isSuperAdminOrganization && (
        <BtButton
          color="white"
          disabled={loading || uiBusy}
          loaderShow={suggesting}
          leftIcon={<SparkleIcon size="16px" />}
          onClick={runSuggestLinks}
        >
          {t('suggest.button', 'Föreslå kopplingar')}
        </BtButton>
      )}
    </SearchBarWithActions>

    {suggestionCategoryCount > 0 && (
      <div className={`${styles.banner} ${suggestionCount ? '' : styles.bannerNoMatch}`.trim()}>
        <SparkleIcon size="16px" color={suggestionCount ? 'var(--green-700)' : 'var(--gray-700)'} />
        <span className={styles.bannerText}>
          {suggestionCount
            ? t('suggest.banner', {
              count: suggestionCategoryCount,
              suggestionCount,
              defaultValue_one: '{{suggestionCount}} förslag för {{count}} kategori utan koppling. Godkänn med ✓, eller öppna en rad för att justera innan du sparar.',
              defaultValue_other: '{{suggestionCount}} förslag för {{count}} kategorier utan koppling. Godkänn med ✓, eller öppna en rad för att justera innan du sparar.',
            })
            : t('suggest.bannerNoMatches', {
              count: suggestionCategoryCount,
              defaultValue_one: 'Ingen träff hittades för {{count}} kategori utan koppling.',
              defaultValue_other: 'Ingen träff hittades för {{count}} kategorier utan koppling.',
            })}
        </span>
        {suggestionCount ? (
          <>
            <BtButton
              color="dark"
              size="xxs"
              style={{ height: '28px', borderRadius: '4px' }}
              disabled={uiBusy}
              loaderShow={acceptingAll}
              onClick={acceptAllSuggestions}
            >
              {t('suggest.acceptAll', 'Godkänn alla')}
            </BtButton>
            <BtButton
              color="white"
              size="xxs"
              disabled={uiBusy}
              onClick={dismissAllSuggestions}
            >
              {t('suggest.dismissAll', 'Avvisa alla')}
            </BtButton>
          </>
        ) : (
          // Closing the no-match banner IS a dismissal: it clears the stored no-match
          // entries, so the banner stays gone across reloads.
          <BtButton
            color="white"
            size="xxs"
            disabled={uiBusy}
            onClick={dismissAllSuggestions}
          >
            {t('suggest.dismissBanner', 'Avvisa')}
          </BtButton>
        )}
      </div>
    )}

    <div className={`${styles.tableWrapper} ${shared.row}`}>
      <DataTable
        tableRef={tableRef}
        isLoading={loading}
        columns={tableColumns}
        data={filteredCategories}
        defaultOrderBy={0}
        defaultOrderDirection="asc"
        defaultOrderByField="label"
        onRowClick={isSuperAdminOrganization ? handleRowClick : undefined}
        detailPanel={renderRowEditor}
        rowStyle={(row: CategoryItem) => (row.value === editingRowId ? { display: 'none' } : {})}
        tableLayout="fixed"
        pageSize={CATEGORIES_PER_PAGE}
      />
    </div>

    <Modal
      key={`addCategory${addModalKey}`}
      title={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
      show={addNewAreaModal}
      setShow={handleCloseAddModal}
      hasCloseButton
    >
      <AddAreaForm
        categories={categories}
        superAdminCategories={superAdminCategories}
        loading={loading}
        isSuperAdminOrganization={isSuperAdminOrganization}
        onSave={handleSaveNewCategory}
        onClose={handleCloseAddModal}
      />
    </Modal>
  </div>;
}

export default AppEditCategories;
